/**
 * Materials query hooks — fetch materials data via React Query.
 * Optimized: longer staleTime + prefetch on hover.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MaterialsAPI from '@/api/materials.api';
import type { Material } from '@/api/types';

/** Query key factory for consistent cache keys */
export const materialsKeys = {
  all: ['materials'] as const,
  byType: (type: string) => ['materials', type] as const,
  collections: ['collections'] as const,
  idioms: ['materials', 'idioms'] as const,
  dictionary: ['materials', 'dictionary'] as const,
};

const PAGE_SIZE = 100;

/** Query key based on all params */
const matKey = (opts: { type?: string; creatorId?: string; excludeCreatorId?: string; access?: string; limit?: number; search?: string }) =>
  ['materials', opts.type ?? '', opts.creatorId ?? '', opts.excludeCreatorId ?? '', opts.access ?? '', opts.limit ?? 0, opts.search ?? ''] as const;

/**
 * Fetch materials with server-side filtering and optional pagination.
 * - For «Мои видео»: pass creatorId → server returns only user's videos
 * - For «Все видео»: pass excludeCreatorId + access='public' → others' public only
 */
export function useMaterials(opts: { type?: string; creatorId?: string; excludeCreatorId?: string; access?: string; limit?: number; search?: string; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: matKey(opts),
    queryFn: async () => {
      const { items, total } = await MaterialsAPI.getUserMaterials({
        type: opts.type,
        creatorId: opts.creatorId,
        excludeCreatorId: opts.excludeCreatorId,
        access: opts.access,
        limit: opts.limit,
        search: opts.search,
      });
      return { items, total, page: 0 };
    },
    staleTime: 5 * 60 * 1000,
    enabled: opts.enabled !== false,
  });
}

/**
 * Load the next page of materials and append to existing results.
 * Only used for "Все видео" (no creatorId) pagination.
 */
export function useLoadMoreMaterials() {
  const queryClient = useQueryClient();
  return async (opts: { type: string; limit: number; search?: string }, currentPage: number): Promise<boolean> => {
    const key = matKey(opts);
    const existing = queryClient.getQueryData<{ items: Material[]; total: number; page: number }>(key);
    if (!existing) return false;
    const limit = opts.limit || PAGE_SIZE;
    const skip = (currentPage + 1) * limit;
    if (skip >= existing.total) return false;
    const { items: newItems, total: newTotal } = await MaterialsAPI.getUserMaterials({ ...opts, skip });
    queryClient.setQueryData(key, {
      items: [...existing.items, ...newItems],
      total: newTotal ?? existing.total,
      page: currentPage + 1,
    });
    return skip + limit < (newTotal ?? existing.total);
  };
}

/** Fetch idioms materials (special collection) */
export function useIdiomsMaterials() {
  return useQuery({
    queryKey: materialsKeys.idioms,
    queryFn: () => MaterialsAPI.getIdiomsMaterials(),
    staleTime: 60 * 60 * 1000, // 1h — rarely changes
  });
}

/** Fetch dictionary materials (special collection) */
export function useDictionaryMaterials() {
  return useQuery({
    queryKey: materialsKeys.dictionary,
    queryFn: () => MaterialsAPI.getDictionaryMaterials(),
    staleTime: 60 * 60 * 1000, // 1h — rarely changes
  });
}

/** Fetch all user collections */
export function useCollections() {
  return useQuery({
    queryKey: materialsKeys.collections,
    queryFn: () => MaterialsAPI.getUserCollections(),
    staleTime: 10 * 60 * 1000,
  });
}

/** Fetch the actual materials inside a specific collection (server-side join) */
export function useCollectionMaterials(collectionId: string | null) {
  return useQuery({
    queryKey: ['collectionMaterials', collectionId],
    queryFn: () => MaterialsAPI.getCollectionMaterials(collectionId!),
    staleTime: 5 * 60 * 1000,
    enabled: !!collectionId,
  });
}

/**
 * Prefetch materials into the cache.
 * Call this on hover over the Materials nav link so data is ready instantly.
 */
export function usePrefetchMaterials() {
  const queryClient = useQueryClient();
  return () => {
    // Prefetch only video (first tab) with limit to avoid 43MB load
    queryClient.prefetchQuery({
      queryKey: matKey({ type: 'video', limit: 150 }),
      queryFn: () => MaterialsAPI.getUserMaterials({ type: 'video', limit: 150 }),
      staleTime: 10 * 60 * 1000,
    });
    queryClient.prefetchQuery({
      queryKey: materialsKeys.collections,
      queryFn: () => MaterialsAPI.getUserCollections(),
      staleTime: 10 * 60 * 1000,
    });
  };
}
